// src/utils/transactionUtils.js
/* global BigInt */
/**
 * Helper functions for working with transactions in the BitDog wallet
 */
import { useTranslation } from 'react-i18next'; // Import useTranslation hook

// Format address for display (truncate)
export const formatAddress = (address, t) => {
  if (!address || typeof address !== 'string') return t ? t('transactions.unknown') : 'Unknown';
  if (address.length < 10) return address; // Return as is if too short
  if (address === 'Contract Creation') return address;
  
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

// Calculate time difference for relative time display
export const getRelativeTime = (timestamp, t) => {
  if (!timestamp) return t ? t('transactions.timeAgo.justNow') : 'Just now';
  
  const now = Date.now();
  const diff = now - timestamp;
  
  // Convert to seconds
  const seconds = Math.floor(diff / 1000);
  
  if (seconds < 60) {
    return t ? t('transactions.timeAgo.justNow') : 'Just now';
  }
  
  // Convert to minutes
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return t 
      ? (minutes === 1 ? t('transactions.timeAgo.minuteAgo') : t('transactions.timeAgo.minutesAgo', { count: minutes }))
      : `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  
  // Convert to hours
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t 
      ? (hours === 1 ? t('transactions.timeAgo.hourAgo') : t('transactions.timeAgo.hoursAgo', { count: hours }))
      : `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  
  // Convert to days
  const days = Math.floor(hours / 24);
  if (days < 30) {
    return t 
      ? (days === 1 ? t('transactions.timeAgo.dayAgo') : t('transactions.timeAgo.daysAgo', { count: days }))
      : `${days} day${days > 1 ? 's' : ''} ago`;
  }
  
  // If older than 30 days, just show the date
  return formatDate(timestamp, t);
};

// Format timestamp to human-readable date
export const formatDate = (timestamp, t) => {
  if (!timestamp) return t ? t('transactions.unknownDate') : 'Unknown Date';
  
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, { 
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format timestamp to human-readable time
export const formatTime = (timestamp, t) => {
  if (!timestamp) return t ? t('transactions.unknownTime') : 'Unknown Time';
  
  const date = new Date(timestamp);
  return date.toLocaleTimeString(undefined, { 
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format transaction value for display
export const formatValue = (value, decimals = 18) => {
  if (!value) return '0';
  
  // If value is "Unlimited" (for approvals), just return it directly
  if (value === 'Unlimited') {
    return value;
  }
  
  try {
    // Convert value to string if it's not already
    const valueStr = value.toString();
    
    // Get the decimal point position based on token decimals
    const decimalPlaces = Math.min(6, Number(decimals)); // Cap display decimals at 6
    
    // Use BigInt for precise conversion from wei
    const divisor = BigInt(10) ** BigInt(Number(decimals));
    
    // Convert to BigInt, divide, then convert to string
    // Handle scientific notation by removing it here
    let bigIntValue;
    try {
      bigIntValue = BigInt(valueStr);
      
      // Convert to string with appropriate decimal places
      // We divide as BigInt, then insert decimal point
      const wholePart = (bigIntValue / divisor).toString();
      const fractionalPart = (bigIntValue % divisor).toString().padStart(Number(decimals), '0');
      
      // Format for display: combine whole + appropriate number of decimal places
      const formattedValue = `${wholePart}.${fractionalPart.substring(0, decimalPlaces)}`;
      
      // Remove trailing zeros and decimal point if necessary
      return formattedValue.replace(/\.?0+$/, '');
    } catch (e) {
      console.warn("BigInt conversion failed, falling back to Number:", e);
      
      // Fallback to Number for small values
      const numValue = Number(valueStr) / Number(`1e${decimals}`);
      return numValue.toFixed(decimalPlaces).replace(/\.?0+$/, '');
    }
  } catch (error) {
    console.error("Error formatting value:", error, value);
    return value.toString();
  }
};

// Get transaction type display text
export const getTransactionTypeText = (type, t) => {
  
  if (!t) {
    // Fallback without translation
    switch (type) {
      case 'SEND':
        return 'Send';
      case 'RECEIVE':
        return 'Receive';
      case 'SWAP':
        return 'Swap';
      case 'APPROVE':
        return 'Approve';
      case 'CONTRACT':
        return 'Contract Interaction';
      default:
        return 'Other';
    }
  }
  
  // With translation
  switch (type) {
    case 'SEND':
      return t('transactions.transactionTypes.send');
    case 'RECEIVE':
      return t('transactions.transactionTypes.receive');
    case 'SWAP':
      return t('transactions.transactionTypes.swap');
    case 'APPROVE':
      return t('transactions.transactionTypes.approve');
    case 'CONTRACT':
      return t('transactions.contractInteraction');
    default:
      return t('transactions.other');
  }
};

// Format gas fee for display
export const formatGasFee = (fee, tokenSymbol) => {
  if (!fee) return '0';
  
  try {
    // Use the improved formatValue function
    return `${formatValue(fee)} ${tokenSymbol}`;
  } catch (error) {
    console.error("Error formatting fee:", error, fee);
    return `0 ${tokenSymbol}`;
  }
};